import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { resolveFrontendApiBase } from '../utils/apiBase';
import {
  Card,
  Button,
  Table,
  Spin,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Space,
  Tag,
  Typography,
  message,
  Modal
} from 'antd';
import adminAxios from './adminAxios';
import './AdminBillingTab.css';

const { Text } = Typography;
const { Option } = Select;

const API_BASE = resolveFrontendApiBase();

export type BillingInterval = 'monthly' | 'yearly';

interface AdminSubscriptionPlan {
  id: string;
  planKey: string;
  planName?: string;
  displayName?: string;
  billingInterval: BillingInterval;
  intervalMonths: number;
  creditsPerInterval: number;
  monthlyEquivalentCredits: number;
  price: number;
  currency: string;
  stripeProductID: string;
  stripePriceID: string;
  isActive: boolean;
  isLegacy: boolean;
  sortOrder?: number;
  metadata?: Record<string, any>;
  [key: string]: any;
}

interface PlanFormValues {
  planKey: string;
  displayName: string;
  billingInterval: BillingInterval;
  intervalMonths?: number;
  creditsPerInterval: number;
  monthlyEquivalentCredits?: number;
  price: number;
  currency: string;
  stripeProductID: string;
  stripePriceID: string;
  isActive: boolean;
  isLegacy: boolean;
  sortOrder?: number;
  annualCredits?: number;
  rolloverDays?: number;
  foreverCreditDiscountPercent?: number;
  foreverCreditCouponId?: string;
  annualStripePriceID?: string;
  referralRefereeBonusCredits?: number;
  referralAffiliateRewardMonthlyCredits?: number;
  referralAffiliateRewardAnnualCredits?: number;
  planGroupKey?: string;
  idealFor?: string;
  monthlyCreditRollover?: string;
  highlight?: boolean;
  isBreakPlan?: boolean;
  subscriberOnly?: boolean;
}

interface PlanSeedDefinition {
  planKey: string;
  displayName: string;
  billingInterval: BillingInterval;
  intervalMonths: number;
  creditsPerInterval: number;
  monthlyEquivalentCredits: number;
  price: number;
  currency: string;
  stripeProductID: string;
  stripePriceID: string;
  isActive: boolean;
  isLegacy: boolean;
  sortOrder?: number;
  metadata?: Record<string, any>;
}

const PLAN_SEED_DATA: PlanSeedDefinition[] = [
  {
    planKey: 'inkling_25',
    displayName: 'Inkling',
    billingInterval: 'monthly',
    intervalMonths: 1,
    creditsPerInterval: 15000,
    monthlyEquivalentCredits: 15000,
    price: 19,
    currency: 'USD',
    stripeProductID: 'prod_OHvRZLzGFNhwt5',
    stripePriceID: 'price_1SFRLWEj6HHcTsCPjReYkIXX',
    isActive: true,
    isLegacy: false,
    sortOrder: 10,
    metadata: {
      annualCredits: 180000,
      rolloverDays: 30,
      foreverCreditDiscountPercent: 0,
      annualStripePriceID: 'price_1SFRLrEj6HHcTsCPYGvfquLo',
      monthlyCredits: 15000,
      referralRefereeBonusCredits: 0,
      referralAffiliateRewardMonthlyCredits: 0,
      referralAffiliateRewardAnnualCredits: 0,
      planGroupKey: 'inkling',
      idealFor: 'Testing the platform or low-volume authors needing occasional translations.',
    },
  },
  {
    planKey: 'inkling_25_annual',
    displayName: 'Inkling (Annual)',
    billingInterval: 'yearly',
    intervalMonths: 12,
    creditsPerInterval: 180000,
    monthlyEquivalentCredits: 15000,
    price: 190,
    currency: 'USD',
    stripeProductID: 'prod_OHvRZLzGFNhwt5',
    stripePriceID: 'price_1SFRLrEj6HHcTsCPYGvfquLo',
    isActive: true,
    isLegacy: false,
    sortOrder: 11,
    metadata: {
      annualCredits: 180000,
      rolloverDays: 30,
      foreverCreditDiscountPercent: 0,
      monthlyCredits: 15000,
      referralRefereeBonusCredits: 0,
      referralAffiliateRewardMonthlyCredits: 0,
      referralAffiliateRewardAnnualCredits: 0,
      planGroupKey: 'inkling',
      idealFor: 'Testing the platform or low-volume authors needing occasional translations.',
    },
  },
  {
    planKey: 'storyteller_25',
    displayName: 'Storyteller',
    billingInterval: 'monthly',
    intervalMonths: 1,
    creditsPerInterval: 50000,
    monthlyEquivalentCredits: 50000,
    price: 49,
    currency: 'USD',
    stripeProductID: 'prod_OHvTGEWGD8tAsV',
    stripePriceID: 'price_1SFRNHEj6HHcTsCP22K2KETx',
    isActive: true,
    isLegacy: false,
    sortOrder: 20,
    metadata: {
      annualCredits: 600000,
      rolloverDays: 30,
      foreverCreditDiscountPercent: 15,
      annualStripePriceID: 'price_1SFRNgEj6HHcTsCPlNKMTF4T',
      monthlyCredits: 50000,
      referralRefereeBonusCredits: 5000,
      referralAffiliateRewardMonthlyCredits: 3750,
      referralAffiliateRewardAnnualCredits: 7500,
      planGroupKey: 'storyteller',
      idealFor: 'Authors translating a steady stream of short stories or novellas each month.',
    },
  },
  {
    planKey: 'storyteller_25_annual',
    displayName: 'Storyteller (Annual)',
    billingInterval: 'yearly',
    intervalMonths: 12,
    creditsPerInterval: 600000,
    monthlyEquivalentCredits: 50000,
    price: 490,
    currency: 'USD',
    stripeProductID: 'prod_OHvTGEWGD8tAsV',
    stripePriceID: 'price_1SFRNgEj6HHcTsCPlNKMTF4T',
    isActive: true,
    isLegacy: false,
    sortOrder: 21,
    metadata: {
      annualCredits: 600000,
      rolloverDays: 30,
      foreverCreditDiscountPercent: 15,
      monthlyCredits: 50000,
      referralRefereeBonusCredits: 5000,
      referralAffiliateRewardMonthlyCredits: 3750,
      referralAffiliateRewardAnnualCredits: 7500,
      planGroupKey: 'storyteller',
      idealFor: 'Authors translating a steady stream of short stories or novellas each month.',
    },
  },
  {
    planKey: 'novelist_25',
    displayName: 'Novelist',
    billingInterval: 'monthly',
    intervalMonths: 1,
    creditsPerInterval: 150000,
    monthlyEquivalentCredits: 150000,
    price: 129,
    currency: 'USD',
    stripeProductID: 'prod_OHvV1uuBNrSmY4',
    stripePriceID: 'price_1SFROSEj6HHcTsCP6QwPOeNT',
    isActive: true,
    isLegacy: false,
    sortOrder: 30,
    metadata: {
      annualCredits: 1800000,
      rolloverDays: 60,
      foreverCreditDiscountPercent: 25,
      annualStripePriceID: 'price_1SFROhEj6HHcTsCPS9nycKYQ',
      monthlyCredits: 150000,
      referralRefereeBonusCredits: 15000,
      referralAffiliateRewardMonthlyCredits: 12500,
      referralAffiliateRewardAnnualCredits: 25000,
      planGroupKey: 'novelist',
      idealFor: 'Novelists releasing full-length books or multiple shorter works every month.',
    },
  },
  {
    planKey: 'novelist_25_annual',
    displayName: 'Novelist (Annual)',
    billingInterval: 'yearly',
    intervalMonths: 12,
    creditsPerInterval: 1800000,
    monthlyEquivalentCredits: 150000,
    price: 1290,
    currency: 'USD',
    stripeProductID: 'prod_OHvV1uuBNrSmY4',
    stripePriceID: 'price_1SFROhEj6HHcTsCPS9nycKYQ',
    isActive: true,
    isLegacy: false,
    sortOrder: 31,
    metadata: {
      annualCredits: 1800000,
      rolloverDays: 60,
      foreverCreditDiscountPercent: 25,
      monthlyCredits: 150000,
      referralRefereeBonusCredits: 15000,
      referralAffiliateRewardMonthlyCredits: 12500,
      referralAffiliateRewardAnnualCredits: 25000,
      planGroupKey: 'novelist',
      idealFor: 'Novelists releasing full-length books or multiple shorter works every month.',
    },
  },
  {
    planKey: 'bestseller_25',
    displayName: 'Bestseller',
    billingInterval: 'monthly',
    intervalMonths: 1,
    creditsPerInterval: 500000,
    monthlyEquivalentCredits: 500000,
    price: 299,
    currency: 'USD',
    stripeProductID: 'prod_PdpiSbGF6oTnnQ',
    stripePriceID: 'price_1SFRPbEj6HHcTsCP55g0bo3T',
    isActive: true,
    isLegacy: false,
    sortOrder: 40,
    metadata: {
      annualCredits: 6000000,
      rolloverDays: 90,
      foreverCreditDiscountPercent: 30,
      annualStripePriceID: 'price_1SFRPzEj6HHcTsCPaXmcfUJQ',
      monthlyCredits: 500000,
      referralRefereeBonusCredits: 50000,
      referralAffiliateRewardMonthlyCredits: 37500,
      referralAffiliateRewardAnnualCredits: 75000,
      planGroupKey: 'bestseller',
      idealFor: 'High-volume teams translating large backlists or multiple series simultaneously.',
    },
  },
  {
    planKey: 'bestseller_25_annual',
    displayName: 'Bestseller (Annual)',
    billingInterval: 'yearly',
    intervalMonths: 12,
    creditsPerInterval: 6000000,
    monthlyEquivalentCredits: 500000,
    price: 2990,
    currency: 'USD',
    stripeProductID: 'prod_PdpiSbGF6oTnnQ',
    stripePriceID: 'price_1SFRPzEj6HHcTsCPaXmcfUJQ',
    isActive: true,
    isLegacy: false,
    sortOrder: 41,
    metadata: {
      annualCredits: 6000000,
      rolloverDays: 90,
      foreverCreditDiscountPercent: 30,
      monthlyCredits: 500000,
      referralRefereeBonusCredits: 50000,
      referralAffiliateRewardMonthlyCredits: 37500,
      referralAffiliateRewardAnnualCredits: 75000,
      planGroupKey: 'bestseller',
      idealFor: 'High-volume teams translating large backlists or multiple series simultaneously.',
    },
  },
  {
    planKey: 'publisher_25',
    displayName: 'Publisher',
    billingInterval: 'monthly',
    intervalMonths: 1,
    creditsPerInterval: 2000000,
    monthlyEquivalentCredits: 2000000,
    price: 999,
    currency: 'USD',
    stripeProductID: 'prod_QNUOk66VPf7VhN',
    stripePriceID: 'price_1SFRQsEj6HHcTsCPrk0XW9nQ',
    isActive: true,
    isLegacy: false,
    sortOrder: 50,
    metadata: {
      annualCredits: 24000000,
      rolloverDays: 180,
      foreverCreditDiscountPercent: 35,
      annualStripePriceID: 'price_1SFRRBEj6HHcTsCPuezbbR4Z',
      monthlyCredits: 2000000,
      referralRefereeBonusCredits: 150000,
      referralAffiliateRewardMonthlyCredits: 125000,
      referralAffiliateRewardAnnualCredits: 250000,
      planGroupKey: 'publisher',
      idealFor: 'Publishers and translation agencies managing multi-language catalogs at scale.',
    },
  },
  {
    planKey: 'publisher_25_annual',
    displayName: 'Publisher (Annual)',
    billingInterval: 'yearly',
    intervalMonths: 12,
    creditsPerInterval: 24000000,
    monthlyEquivalentCredits: 2000000,
    price: 9990,
    currency: 'USD',
    stripeProductID: 'prod_QNUOk66VPf7VhN',
    stripePriceID: 'price_1SFRRBEj6HHcTsCPuezbbR4Z',
    isActive: true,
    isLegacy: false,
    sortOrder: 51,
    metadata: {
      annualCredits: 24000000,
      rolloverDays: 180,
      foreverCreditDiscountPercent: 35,
      monthlyCredits: 2000000,
      referralRefereeBonusCredits: 150000,
      referralAffiliateRewardMonthlyCredits: 125000,
      referralAffiliateRewardAnnualCredits: 250000,
      planGroupKey: 'publisher',
      idealFor: 'Publishers and translation agencies managing multi-language catalogs at scale.',
    },
  },
];

const MANAGED_METADATA_KEYS = [
  'annualCredits',
  'rolloverDays',
  'foreverCreditDiscountPercent',
  'foreverCreditCouponId',
  'annualStripePriceID',
  'referralRefereeBonusCredits',
  'referralAffiliateRewardMonthlyCredits',
  'referralAffiliateRewardAnnualCredits',
  'planGroupKey',
  'idealFor',
  'monthlyCreditRollover',
  'highlight',
  'isBreakPlan',
  'subscriberOnly',
];

const ON_BREAK_PLAN_DEFAULTS: Partial<PlanFormValues> = {
  planKey: 'on_break',
  displayName: 'On Break',
  billingInterval: 'monthly',
  intervalMonths: 1,
  creditsPerInterval: 0,
  monthlyEquivalentCredits: 0,
  annualCredits: 0,
  rolloverDays: 0,
  foreverCreditDiscountPercent: 0,
  price: 3.99,
  currency: 'USD',
  stripeProductID: 'prod_UPpK4JaS2kr0NM',
  stripePriceID: 'price_1TUWTvEj6HHcTsCPlloksVKd',
  isActive: true,
  isLegacy: false,
  sortOrder: 999,
  planGroupKey: 'on_break',
  idealFor: 'Keeping existing credits protected between projects',
  monthlyCreditRollover: 'Credits do not expire while subscribed',
  highlight: false,
  isBreakPlan: true,
  subscriberOnly: true,
};

const formSectionStyle: React.CSSProperties = {
  padding: 14,
};

const formSectionHeaderStyle: React.CSSProperties = {
  fontWeight: 600,
  marginBottom: 12,
};

const formGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '0 16px',
};

const formGridFullWidthStyle: React.CSSProperties = {
  gridColumn: '1 / -1',
};

const PlanFormSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="admin-billing-plan-form-section" style={formSectionStyle}>
    <div className="admin-billing-plan-form-section__title" style={formSectionHeaderStyle}>{title}</div>
    <div style={formGridStyle}>{children}</div>
  </div>
);

const normalizeNumericMetadataValue = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  if (typeof value === 'number') {
    return Number.isNaN(value) ? undefined : value;
  }
  if (typeof value === 'bigint') {
    return Number(value);
  }
  if (typeof value === 'string') {
    const normalized = value.replace(/,/g, '').trim();
    if (!normalized) {
      return undefined;
    }
    const numericValue = Number(normalized);
    return Number.isNaN(numericValue) ? undefined : numericValue;
  }
  return undefined;
};

interface AdminBillingTabProps {
  usersSharedState: any;
}

const sortSubscriptionPlans = (plans: AdminSubscriptionPlan[]): AdminSubscriptionPlan[] => {
  return [...plans].sort((a, b) => {
    const legacyDiff = Number(Boolean(a.isLegacy)) - Number(Boolean(b.isLegacy));
    if (legacyDiff !== 0) {
      return legacyDiff;
    }

    const orderA = typeof a.sortOrder === 'number' ? a.sortOrder : Number.MAX_SAFE_INTEGER;
    const orderB = typeof b.sortOrder === 'number' ? b.sortOrder : Number.MAX_SAFE_INTEGER;
    const orderDiff = orderA - orderB;
    if (orderDiff !== 0) {
      return orderDiff;
    }

    const nameA = (a.displayName || a.planName || a.planKey || '').toLowerCase();
    const nameB = (b.displayName || b.planName || b.planKey || '').toLowerCase();
    if (nameA && nameB) {
      return nameA.localeCompare(nameB);
    }
    return 0;
  });
};

const AdminBillingTab: React.FC<AdminBillingTabProps> = ({ usersSharedState }) => {
  const [subscriptionPlans, setSubscriptionPlans] = useState<AdminSubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [isPlanModalVisible, setIsPlanModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState<AdminSubscriptionPlan | null>(null);
  const [planSubmitLoading, setPlanSubmitLoading] = useState(false);
  const [isPopulatingPlans, setIsPopulatingPlans] = useState(false);
  const [planForm] = Form.useForm<PlanFormValues>();

  const fetchSubscriptionPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const resp = await adminAxios.get<{ subscriptionPlans: AdminSubscriptionPlan[] }>(
        `${API_BASE}/admin/subscription-plans`,
        {
          params: {
            includeLegacy: true,
            includeInactive: true,
          },
        }
      );
      const plans = Array.isArray(resp.data?.subscriptionPlans)
        ? resp.data.subscriptionPlans
        : [];
      setSubscriptionPlans(sortSubscriptionPlans(plans));
    } catch (error) {
      console.error('Failed to load subscription plans', error);
      message.error('Failed to load subscription plans');
    } finally {
      setPlansLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionPlans();
  }, [fetchSubscriptionPlans]);

  const resolvePlanId = useCallback((plan: Partial<AdminSubscriptionPlan> | null | undefined) => {
    if (!plan) {
      return undefined;
    }

    const candidates = [
      (plan as any)?.id,
      (plan as any)?.docId,
      (plan as any)?.documentId,
      (plan as any)?.documentID,
    ];

    for (const candidate of candidates) {
      if (candidate === undefined || candidate === null) {
        continue;
      }
      const id = String(candidate).trim();
      if (id.length > 0) {
        return id;
      }
    }

    return undefined;
  }, []);

  const openCreatePlanModal = useCallback(() => {
    setEditingPlan(null);
    planForm.resetFields();
    planForm.setFieldsValue({
      planKey: '',
      displayName: '',
      billingInterval: 'monthly',
      intervalMonths: 1,
      creditsPerInterval: 0,
      monthlyEquivalentCredits: undefined,
      price: 0,
      currency: 'USD',
      stripeProductID: '',
      stripePriceID: '',
      isActive: true,
      isLegacy: false,
      sortOrder: undefined,
      annualCredits: undefined,
      rolloverDays: undefined,
      foreverCreditDiscountPercent: undefined,
      foreverCreditCouponId: '',
      annualStripePriceID: '',
      referralRefereeBonusCredits: undefined,
      referralAffiliateRewardMonthlyCredits: undefined,
      referralAffiliateRewardAnnualCredits: undefined,
      planGroupKey: '',
      idealFor: '',
      monthlyCreditRollover: '',
      highlight: false,
      isBreakPlan: false,
      subscriberOnly: false,
    });
    setIsPlanModalVisible(true);
  }, [planForm]);

  const openEditPlanModal = useCallback(
    (plan: AdminSubscriptionPlan) => {
      setEditingPlan(plan);
      planForm.resetFields();
      const metadata = plan.metadata || {};
      planForm.setFieldsValue({
        planKey: plan.planKey,
        displayName: plan.displayName || plan.planName || plan.planKey,
        billingInterval: plan.billingInterval,
        intervalMonths: plan.intervalMonths,
        creditsPerInterval: plan.creditsPerInterval,
        monthlyEquivalentCredits: plan.monthlyEquivalentCredits,
        price: plan.price,
        currency: (plan.currency || 'USD').toUpperCase(),
        stripeProductID: plan.stripeProductID,
        stripePriceID: plan.stripePriceID,
        isActive: plan.isActive,
        isLegacy: plan.isLegacy,
        sortOrder: plan.sortOrder,
        annualCredits:
          normalizeNumericMetadataValue(metadata.annualCredits),
        rolloverDays:
          normalizeNumericMetadataValue(metadata.rolloverDays),
        foreverCreditDiscountPercent:
          normalizeNumericMetadataValue(metadata.foreverCreditDiscountPercent),
        foreverCreditCouponId:
          typeof metadata.foreverCreditCouponId === 'string' ? metadata.foreverCreditCouponId : '',
        annualStripePriceID:
          typeof metadata.annualStripePriceID === 'string' ? metadata.annualStripePriceID : '',
        referralRefereeBonusCredits: normalizeNumericMetadataValue(metadata.referralRefereeBonusCredits),
        referralAffiliateRewardMonthlyCredits: normalizeNumericMetadataValue(
          metadata.referralAffiliateRewardMonthlyCredits
        ),
        referralAffiliateRewardAnnualCredits: normalizeNumericMetadataValue(
          metadata.referralAffiliateRewardAnnualCredits
        ),
        planGroupKey: typeof metadata.planGroupKey === 'string' ? metadata.planGroupKey : '',
        idealFor: typeof metadata.idealFor === 'string' ? metadata.idealFor : '',
        monthlyCreditRollover:
          typeof metadata.monthlyCreditRollover === 'string' ? metadata.monthlyCreditRollover : '',
        highlight: Boolean(metadata.highlight),
        isBreakPlan: Boolean(metadata.isBreakPlan),
        subscriberOnly: Boolean(metadata.subscriberOnly),
      });
      setIsPlanModalVisible(true);
    },
    [planForm]
  );

  const applyOnBreakDefaults = useCallback(() => {
    planForm.setFieldsValue(ON_BREAK_PLAN_DEFAULTS);
  }, [planForm]);

  const closePlanModal = useCallback(() => {
    setIsPlanModalVisible(false);
    setEditingPlan(null);
    planForm.resetFields();
  }, [planForm]);

  const handlePlanToggle = useCallback(
    async (plan: AdminSubscriptionPlan, field: 'isActive' | 'isLegacy', value: boolean) => {
      const planId = resolvePlanId(plan);
      if (!planId) {
        message.error('Unable to update plan because its identifier is missing. Reload plans and try again.');
        fetchSubscriptionPlans();
        return;
      }
      try {
        await adminAxios.patch(`${API_BASE}/admin/subscription-plans/${planId}`, {
          [field]: value,
        });
        message.success(`Plan ${field === 'isActive' ? 'status' : 'legacy flag'} updated`);
        fetchSubscriptionPlans();
      } catch (error: any) {
        const detail = error?.response?.data?.detail || error?.message || `Failed to update plan ${field}`;
        message.error(detail);
      }
    },
    [fetchSubscriptionPlans, resolvePlanId]
  );

  const handlePlanSubmit = useCallback(async () => {
    try {
      const values = await planForm.validateFields();
      const payload: Record<string, any> = {
        planKey: values.planKey?.trim(),
        displayName: values.displayName?.trim(),
        billingInterval: values.billingInterval,
        intervalMonths:
          values.intervalMonths !== undefined && values.intervalMonths !== null
            ? Number(values.intervalMonths)
            : undefined,
        creditsPerInterval: Number(values.creditsPerInterval),
        monthlyEquivalentCredits:
          values.monthlyEquivalentCredits != null ? Number(values.monthlyEquivalentCredits) : undefined,
        price: Number(values.price),
        currency: values.currency?.trim()?.toUpperCase(),
        stripeProductID: values.stripeProductID?.trim(),
        stripePriceID: values.stripePriceID?.trim(),
        isActive: values.isActive,
        isLegacy: values.isLegacy,
        sortOrder: values.sortOrder,
      };

      const metadataPayload: Record<string, any> = {};
      if (editingPlan?.metadata) {
        Object.entries(editingPlan.metadata).forEach(([key, value]) => {
          if (!MANAGED_METADATA_KEYS.includes(key)) {
            metadataPayload[key] = value;
          }
        });
      }

      const assignNumberMetadata = (formValue: unknown, metadataKey: string) => {
        const numericValue = normalizeNumericMetadataValue(formValue);
        if (numericValue !== undefined) {
          metadataPayload[metadataKey] = numericValue;
        }
      };

      assignNumberMetadata(values.annualCredits, 'annualCredits');
      assignNumberMetadata(values.rolloverDays, 'rolloverDays');
      assignNumberMetadata(values.foreverCreditDiscountPercent, 'foreverCreditDiscountPercent');
      assignNumberMetadata(values.referralRefereeBonusCredits, 'referralRefereeBonusCredits');
      assignNumberMetadata(values.referralAffiliateRewardMonthlyCredits, 'referralAffiliateRewardMonthlyCredits');
      assignNumberMetadata(values.referralAffiliateRewardAnnualCredits, 'referralAffiliateRewardAnnualCredits');

      if (typeof values.highlight === 'boolean') {
        metadataPayload.highlight = values.highlight;
      }
      if (typeof values.isBreakPlan === 'boolean') {
        metadataPayload.isBreakPlan = values.isBreakPlan;
      }
      if (typeof values.subscriberOnly === 'boolean') {
        metadataPayload.subscriberOnly = values.subscriberOnly;
      }

      if (values.annualStripePriceID && values.annualStripePriceID.trim()) {
        metadataPayload.annualStripePriceID = values.annualStripePriceID.trim();
      }
      if (values.planGroupKey && values.planGroupKey.trim()) {
        metadataPayload.planGroupKey = values.planGroupKey.trim();
      }
      if (values.idealFor && values.idealFor.trim()) {
        metadataPayload.idealFor = values.idealFor.trim();
      }
      if (values.monthlyCreditRollover !== undefined) {
        const trimmedRollover =
          typeof values.monthlyCreditRollover === 'string' ? values.monthlyCreditRollover.trim() : '';
        if (trimmedRollover) {
          metadataPayload.monthlyCreditRollover = trimmedRollover;
        } else if (editingPlan?.metadata?.monthlyCreditRollover) {
          metadataPayload.monthlyCreditRollover = null;
        }
      }
      if (values.foreverCreditCouponId !== undefined) {
        const trimmedCoupon =
          typeof values.foreverCreditCouponId === 'string' ? values.foreverCreditCouponId.trim() : '';
        if (trimmedCoupon) {
          metadataPayload.foreverCreditCouponId = trimmedCoupon;
        } else if (editingPlan?.metadata?.foreverCreditCouponId) {
          metadataPayload.foreverCreditCouponId = null;
        }
      }

      Object.keys(metadataPayload).forEach((key) => {
        const val = metadataPayload[key];
        if (val === undefined || val === '') {
          delete metadataPayload[key];
        }
      });

      const hadManagedMetadata = MANAGED_METADATA_KEYS.some(
        (key) => editingPlan?.metadata?.[key] !== undefined && editingPlan?.metadata?.[key] !== null
      );

      if (Object.keys(metadataPayload).length > 0 || hadManagedMetadata) {
        payload.metadata = metadataPayload;
      }

      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined || payload[key] === null || payload[key] === '') {
          delete payload[key];
        }
      });

      let editingPlanId: string | undefined;
      if (editingPlan) {
        delete payload.planKey;
        editingPlanId = resolvePlanId(editingPlan);
        if (!editingPlanId) {
          message.error('Unable to update plan because its identifier is missing. Reload plans and try again.');
          fetchSubscriptionPlans();
          return;
        }
      }

      setPlanSubmitLoading(true);
      if (editingPlan) {
        await adminAxios.patch(`${API_BASE}/admin/subscription-plans/${editingPlanId}`, payload);
        message.success('Plan updated');
      } else {
        await adminAxios.post(`${API_BASE}/admin/subscription-plans`, payload);
        message.success('Plan created');
      }
      closePlanModal();
      fetchSubscriptionPlans();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      const detail = error?.response?.data?.detail || error?.message || 'Failed to save plan';
      message.error(detail);
    } finally {
      setPlanSubmitLoading(false);
    }
  }, [closePlanModal, editingPlan, fetchSubscriptionPlans, planForm, resolvePlanId]);

  const populatePlans = useCallback(async () => {
    setIsPopulatingPlans(true);
    try {
      const existingByPlanKey = new Map<string, AdminSubscriptionPlan>();
      const existingByNameAndInterval = new Map<string, AdminSubscriptionPlan>();
      subscriptionPlans.forEach((plan) => {
        if (plan.planKey) {
          existingByPlanKey.set(plan.planKey, plan);
        }
        const nameKey = `${(plan.displayName || plan.planName || '').toLowerCase()}|${plan.billingInterval}`;
        if (nameKey.trim() !== '|') {
          existingByNameAndInterval.set(nameKey, plan);
        }
      });

      const createdPlanKeys: string[] = [];
      const updatedPlanKeys: string[] = [];

      for (const seed of PLAN_SEED_DATA) {
        const basePayload: Record<string, any> = {
          ...seed,
        };
        basePayload.currency = (basePayload.currency || 'USD').toUpperCase();

        if (seed.metadata) {
          basePayload.metadata = { ...seed.metadata };
        }

        Object.keys(basePayload).forEach((key) => {
          if (basePayload[key] === undefined || basePayload[key] === null || basePayload[key] === '') {
            delete basePayload[key];
          }
        });

        let existingPlan = existingByPlanKey.get(seed.planKey);
        if (!existingPlan) {
          const key = `${seed.displayName.toLowerCase()}|${seed.billingInterval}`;
          existingPlan = existingByNameAndInterval.get(key);
        }

        if (existingPlan) {
          const planId = resolvePlanId(existingPlan);
          if (!planId) {
            message.warning(`Skipping ${seed.planKey} because its document id could not be resolved.`);
            continue;
          }
          await adminAxios.patch(`${API_BASE}/admin/subscription-plans/${planId}`, basePayload);
          updatedPlanKeys.push(seed.planKey);
          continue;
        }

        await adminAxios.post(`${API_BASE}/admin/subscription-plans`, basePayload);
        createdPlanKeys.push(seed.planKey);
      }

      if (createdPlanKeys.length || updatedPlanKeys.length) {
        const parts: string[] = [];
        if (createdPlanKeys.length) {
          parts.push(`created ${createdPlanKeys.length}`);
        }
        if (updatedPlanKeys.length) {
          parts.push(`updated ${updatedPlanKeys.length}`);
        }
        message.success(`Subscription plans populated (${parts.join(', ')})`);
      } else {
        message.info('No subscription plans were created.');
      }
      fetchSubscriptionPlans();
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.message || 'Failed to populate plans';
      message.error(detail);
      throw error;
    } finally {
      setIsPopulatingPlans(false);
    }
  }, [fetchSubscriptionPlans, resolvePlanId, subscriptionPlans]);

  const handlePopulatePlansClick = useCallback(() => {
    Modal.confirm({
      title: 'Populate subscription plans',
      content:
        'This will upsert the Inkling through Publisher plans (monthly and annual), updating existing plans with the latest pricing, rollover, and referral metadata. Continue?',
      okText: 'Populate',
      cancelText: 'Cancel',
      onOk: () => populatePlans(),
    });
  }, [populatePlans]);

  const planColumns = useMemo(() => [
    {
      title: 'Plan',
      key: 'planName',
      render: (_: unknown, record: AdminSubscriptionPlan) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.displayName || record.planName || record.planKey}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.planKey}</Text>
        </Space>
      ),
    },
    {
      title: 'Interval',
      dataIndex: 'billingInterval',
      key: 'billingInterval',
      render: (_: BillingInterval, record: AdminSubscriptionPlan) => (
        <span>
          {record.billingInterval === 'yearly' ? 'Yearly' : 'Monthly'}
          {record.intervalMonths ? ` (${record.intervalMonths} months)` : ''}
        </span>
      ),
    },
    {
      title: 'Credits',
      key: 'creditsPerInterval',
      render: (_: unknown, record: AdminSubscriptionPlan) => {
        const metadata = record.metadata || {};
        const annualCredits = metadata.annualCredits;
        const monthlyEquivalent = record.monthlyEquivalentCredits || metadata.monthlyCredits;
        const intervalLabel = record.billingInterval === 'yearly' ? 'year' : 'month';
        return (
          <Space direction="vertical" size={0}>
            <span>{record.creditsPerInterval.toLocaleString()} per {intervalLabel}</span>
            {annualCredits && record.billingInterval !== 'yearly' ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Annual: {Number(annualCredits).toLocaleString()}
              </Text>
            ) : null}
            {monthlyEquivalent ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Monthly eq: {Number(monthlyEquivalent).toLocaleString()}
              </Text>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: 'Program Details',
      key: 'programDetails',
      render: (_: unknown, record: AdminSubscriptionPlan) => {
        const metadata = record.metadata || {};
        const rolloverDays = metadata.rolloverDays;
        const discount = metadata.foreverCreditDiscountPercent;
        const annualStripePriceID = metadata.annualStripePriceID;
        const referralBonus = metadata.referralRefereeBonusCredits;
        const affiliateMonthly = metadata.referralAffiliateRewardMonthlyCredits;
        const affiliateAnnual = metadata.referralAffiliateRewardAnnualCredits;
        return (
          <Space direction="vertical" size={0}>
            <span>Rollover: {rolloverDays ? `${Number(rolloverDays).toLocaleString()} days` : '—'}</span>
            <span>Forever discount: {discount !== undefined ? `${Number(discount)}%` : '—'}</span>
            <span>
              Forever coupon:{' '}
              {metadata.foreverCreditCouponId ? String(metadata.foreverCreditCouponId) : '—'}
            </span>
            {annualStripePriceID ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Annual Price ID: {annualStripePriceID}
              </Text>
            ) : null}
            {referralBonus ? (
              <span>Referral bonus: {Number(referralBonus).toLocaleString()} credits</span>
            ) : null}
            {affiliateMonthly ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Affiliate monthly: {Number(affiliateMonthly).toLocaleString()}
              </Text>
            ) : null}
            {affiliateAnnual ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Affiliate annual: {Number(affiliateAnnual).toLocaleString()}
              </Text>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price: number, record: AdminSubscriptionPlan) => {
        const currency = (record.currency || 'USD').toUpperCase();
        let formatted = `${currency} ${Number(price ?? 0).toFixed(2)}`;
        try {
          formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
          }).format(Number(price ?? 0));
        } catch (err) {
          // ignore formatting errors
        }
        return (
          <Space direction="vertical" size={0}>
            <span>{formatted}</span>
            <Text type="secondary" style={{ fontSize: 12 }}>Product: {record.stripeProductID}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>Price: {record.stripePriceID}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (value: boolean, record: AdminSubscriptionPlan) => (
        <Switch
          size="small"
          checked={value}
          onChange={(checked) => handlePlanToggle(record, 'isActive', checked)}
        />
      ),
    },
    {
      title: 'Legacy',
      dataIndex: 'isLegacy',
      key: 'isLegacy',
      render: (value: boolean, record: AdminSubscriptionPlan) => (
        <Switch
          size="small"
          checked={value}
          onChange={(checked) => handlePlanToggle(record, 'isLegacy', checked)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: AdminSubscriptionPlan) => (
        <Button type="link" onClick={() => openEditPlanModal(record)}>
          Edit
        </Button>
      ),
    },
  ], [handlePlanToggle, openEditPlanModal]);

  return (
    <div className="admin-billing-tab">
      <section className="admin-billing-header" aria-labelledby="admin-billing-title">
        <div>
          <div className="admin-billing-kicker">Billing configuration</div>
          <h2 id="admin-billing-title">Subscription Plans</h2>
          <p>Review active, legacy, and subscriber-only plan records before changing Stripe-linked billing details.</p>
        </div>
        <div className="admin-billing-actions">
          <span className="admin-billing-count" aria-live="polite">
            {subscriptionPlans.length} plan{subscriptionPlans.length === 1 ? '' : 's'}
          </span>
          <Button onClick={handlePopulatePlansClick} loading={isPopulatingPlans}>
            Populate Plans
          </Button>
          <Button type="primary" onClick={openCreatePlanModal}>
            New Plan
          </Button>
        </div>
      </section>

      <section className="admin-billing-table-section">
        <Spin spinning={plansLoading}>
          <Table<AdminSubscriptionPlan>
            dataSource={subscriptionPlans}
            columns={planColumns}
            rowKey={(plan) => resolvePlanId(plan) || `${plan.planKey}-${plan.stripeProductID || plan.stripePriceID || 'unknown'}`}
            pagination={false}
            scroll={{ x: 1120 }}
            locale={{ emptyText: 'No plans found' }}
          />
        </Spin>
      </section>

      <Modal
        title={editingPlan ? 'Edit Subscription Plan' : 'Create Subscription Plan'}
        visible={isPlanModalVisible}
        onCancel={closePlanModal}
        onOk={handlePlanSubmit}
        confirmLoading={planSubmitLoading}
        width={860}
        destroyOnClose
        className="admin-billing-plan-modal"
      >
        <Form form={planForm} layout="vertical">
          <div className="admin-billing-modal-readiness">
            <span>{editingPlan ? 'Review Stripe IDs before saving changes.' : 'Required identity, price, and Stripe fields must be completed.'}</span>
            <Button onClick={applyOnBreakDefaults} disabled={Boolean(editingPlan)}>
              Use On Break Defaults
            </Button>
          </div>

          <PlanFormSection title="Plan Identity">
            <Form.Item
              label="Plan Key"
              name="planKey"
              rules={[{ required: true, message: 'Plan key is required' }]}
            >
              <Input placeholder="e.g. inkling" disabled={Boolean(editingPlan)} />
            </Form.Item>
            <Form.Item
              label="Display Name"
              name="displayName"
              rules={[{ required: true, message: 'Display name is required' }]}
            >
              <Input placeholder="Plan display name" />
            </Form.Item>
            <Form.Item label="Plan Group Key" name="planGroupKey">
              <Input placeholder="e.g. storyteller" />
            </Form.Item>
            <Form.Item label="Sort Order" name="sortOrder">
              <InputNumber precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </PlanFormSection>

          <PlanFormSection title="Billing And Credits">
            <Form.Item
              label="Billing Interval"
              name="billingInterval"
              rules={[{ required: true, message: 'Billing interval is required' }]}
            >
              <Select>
                <Option value="monthly">Monthly</Option>
                <Option value="yearly">Yearly</Option>
              </Select>
            </Form.Item>
            <Form.Item label="Interval Months" name="intervalMonths">
              <InputNumber min={1} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              label="Credits Per Interval"
              name="creditsPerInterval"
              rules={[{ required: true, message: 'Enter credits per interval' }]}
            >
              <InputNumber min={0} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Monthly Equivalent Credits" name="monthlyEquivalentCredits">
              <InputNumber min={0} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Annual Credits" name="annualCredits">
              <InputNumber min={0} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Rollover (days)" name="rolloverDays">
              <InputNumber min={0} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              label="Price"
              name="price"
              rules={[{ required: true, message: 'Enter a price' }]}
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              label="Currency"
              name="currency"
              rules={[{ required: true, message: 'Currency is required' }]}
            >
              <Input placeholder="USD" maxLength={10} />
            </Form.Item>
          </PlanFormSection>

          <PlanFormSection title="Stripe">
            <Form.Item
              label="Stripe Product ID"
              name="stripeProductID"
              rules={[{ required: true, message: 'Stripe product ID is required' }]}
            >
              <Input placeholder="prod_..." />
            </Form.Item>
            <Form.Item
              label="Stripe Price ID"
              name="stripePriceID"
              rules={[{ required: true, message: 'Stripe price ID is required' }]}
            >
              <Input placeholder="price_..." />
            </Form.Item>
            <Form.Item label="Annual Stripe Price ID" name="annualStripePriceID">
              <Input placeholder="price_..." />
            </Form.Item>
            <Form.Item label="Forever Credit Coupon ID" name="foreverCreditCouponId">
              <Input placeholder="Optional Stripe coupon ID" />
            </Form.Item>
          </PlanFormSection>

          <PlanFormSection title="Metadata">
            <Form.Item label="Forever Credit Discount (%)" name="foreverCreditDiscountPercent">
              <InputNumber min={0} max={100} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Referral Bonus (referee)" name="referralRefereeBonusCredits">
              <InputNumber min={0} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Affiliate Reward (monthly signup)" name="referralAffiliateRewardMonthlyCredits">
              <InputNumber min={0} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Affiliate Reward (annual signup)" name="referralAffiliateRewardAnnualCredits">
              <InputNumber min={0} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Subscription Credit Protection" name="monthlyCreditRollover" style={formGridFullWidthStyle}>
              <Input placeholder="Credits do not expire while subscribed; 60-day grace after cancellation" />
            </Form.Item>
            <Form.Item label="Ideal For" name="idealFor" style={formGridFullWidthStyle}>
              <Input.TextArea rows={2} placeholder="Who is this plan ideal for?" />
            </Form.Item>
          </PlanFormSection>

          <PlanFormSection title="Visibility">
            <Form.Item label="Active" name="isActive" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="Legacy" name="isLegacy" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="Highlight" name="highlight" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="On Break Plan" name="isBreakPlan" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="Subscriber Only" name="subscriberOnly" valuePropName="checked">
              <Switch />
            </Form.Item>
          </PlanFormSection>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminBillingTab;
